import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
} from 'react';
import { useIsMobile } from '@/hooks/useMobile';
import { useUIPreference } from '@/hooks/useUIPreference';
import { TooltipProvider } from '@marine/ui/tooltip';

const HEADER_HEIGHT = "60px";
// NOTE: styles/layout.css also declares a --sidebar-width (16rem), but this
// value — spread into an inline style on the layout wrapper — overrides it.
// That CSS declaration is dead; change the width here and in the per-portal
// LayoutProvider style props, not there.
const SIDEBAR_WIDTH = "300px";
const SIDEBAR_COLLAPSED_WIDTH = "60px";
const TOOLBAR_HEIGHT = "54px";

// Define the shape of the layout state
interface LayoutState {
  style: React.CSSProperties;
  bodyClassName: string;
  isMobile: boolean;
  isSidebarOpen: boolean;
  sidebarToggle: () => void;
}

// Create the context
const LayoutContext = createContext<LayoutState | undefined>(undefined);

// Provider component
interface LayoutProviderProps {
  children: ReactNode;
  style?: React.CSSProperties;
  bodyClassName?: string;
}

export function LayoutProvider({ children, style: customStyle, bodyClassName = '' }: LayoutProviderProps) {
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useUIPreference<boolean>('sidebar', true);

  const defaultStyle: React.CSSProperties = {
    '--sidebar-width': SIDEBAR_WIDTH,
    '--sidebar-collapsed-width': SIDEBAR_COLLAPSED_WIDTH,
    '--header-height': HEADER_HEIGHT,
    '--toolbar-height': TOOLBAR_HEIGHT,
  } as React.CSSProperties;

  const style: React.CSSProperties = {
    ...defaultStyle,
    ...customStyle,
  };

  // Sidebar toggle function. usePreference's setter doesn't accept a functional
  // updater, so read the current value from closure.
  const sidebarToggle = () => setIsSidebarOpen(!isSidebarOpen);

  // Set body className on mount and clean up on unmount
  useEffect(() => {
    if (bodyClassName) {
      const body = document.body;
      const existingClasses = body.className;

      // Add new classes
      body.className = `${existingClasses} ${bodyClassName}`.trim();

      // Cleanup function to remove classes on unmount
      return () => {
        body.className = existingClasses;
      };
    }
  }, [bodyClassName]);

  return (
    <LayoutContext.Provider
      value={{
        bodyClassName,
        style,
        isMobile,
        isSidebarOpen,
        sidebarToggle
      }}
    >
      <div
        data-slot="layout-wrapper"
        // #1095 — `min-w-0` overrides the flexbox `min-width: auto` default,
        // which otherwise freezes this at its content's min-content width and
        // makes the whole shell overflow the window instead of compressing.
        className="flex grow min-w-0"
        data-sidebar-open={isSidebarOpen}
        style={style}
      >
        <TooltipProvider delayDuration={0}>
          {children}
        </TooltipProvider>
      </div>
    </LayoutContext.Provider>
  );
}

// Custom hook for consuming the context
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
